import type L from 'leaflet';
import type { Map as LeafletMap, LayerGroup } from 'leaflet';
import MapMarkerCreator from './MapMarkerCreator';

type GroupMarkerClickHandlerParams = {
    map: LeafletMap;
    location: GroupedLocation;
};

type UpdateMapGroupedLocationsParams = {
    groupedLocations: GroupedLocation[];
    Leaflet: typeof L;
    layerGroup: LayerGroup;
    map: LeafletMap;
};

export default class MapGroupedLocationsUpdater {
    public static readonly maximumZoomLevel: number = 14;

    private static readonly increaseZoomLevel: number = 2;

    public static async updateMapGroupedLocations({
        groupedLocations,
        Leaflet,
        layerGroup,
        map
    }: UpdateMapGroupedLocationsParams): Promise<void> {
        if (!map || !Leaflet || !layerGroup) {
            return;
        }

        layerGroup.clearLayers();

        for (const location of groupedLocations) {
            const { geoPoint } = location;

            const marker = MapMarkerCreator.createLeafletMarker({
                lat: geoPoint.lat,
                lon: geoPoint.lon,
                draggable: false
            });

            marker.bindTooltip(MapGroupedLocationsUpdater.getTooltipContent(location), {
                permanent: true,
                offset: [0, -4],
                direction: 'center',
                className: 'nh-group-marker'
            });

            marker.addEventListener('click', (): void =>
                MapGroupedLocationsUpdater.groupMarkerClickHandler({
                    map,
                    location
                })
            );

            layerGroup.addLayer(marker);
        }

        layerGroup.addTo(map);
    }

    private static getTooltipContent(location: GroupedLocation): string {
        const { numberOfHouses } = location;
        return `${numberOfHouses < 100 ? numberOfHouses : '99+'}`;
    }

    private static readonly groupMarkerClickHandler = ({
        map,
        location
    }: GroupMarkerClickHandlerParams): void => {
        const { geoPoint, numberOfHouses } = location;

        if (!map || map.getZoom() >= MapGroupedLocationsUpdater.maximumZoomLevel) {
            return;
        }

        if (numberOfHouses === 1) {
            map.flyTo([geoPoint.lat, geoPoint.lon], MapGroupedLocationsUpdater.maximumZoomLevel);
            return;
        }

        map.flyTo(
            [geoPoint.lat, geoPoint.lon],
            map.getZoom() + MapGroupedLocationsUpdater.increaseZoomLevel
        );
    };
}
